import { Box, Heading, Text, Button, Flex, Spinner } from "@chakra-ui/react";
import React, { useContext, useEffect, useState } from "react";
import Lottie from "react-lottie-player";
import { axiosErrorHandler } from "../../../../api/utils/error";
import { EmailOutline2 } from "../../../../assets/icons/components";
import { AppContext, ApplicationActionType } from "../../../../context/ApplicationContext";
import OTPModal from "../../auth/OTPModal";
import ModalTemplate from "../ModalTemplate";
import useSnackBar from "../SnackBar";
import EmailEdit from "./EmailEdit";
import successLottie from "../../../../assets/successLottie.json";
import InputCustom from "../InputCustom";
import { sendEmailOtp, verifyEmailOtp } from "../../../../api/utils/api";

interface Props {
  showEmailVerifyModal?: boolean;
  setShowEmailVerifyModal?: (showEmailVerifyModal: boolean) => void;
  isOTPModalOpen?: boolean;
  setIsOTPModalOpen?: (isOTPModalOpen: boolean) => void;
  showVerificationSuccessModal?: boolean;
  setShowVerificationSuccessModal?: (showVerificationSuccessModal: boolean) => void;
  showUpdateEmailModal?: boolean;
  setShowUpdateEmailModal?: (showUpdateEmailModal: boolean) => void;
}

const EmailVerifyModal = ({
  showEmailVerifyModal,
  setShowEmailVerifyModal,
  isOTPModalOpen,
  setIsOTPModalOpen,
  showVerificationSuccessModal,
  setShowVerificationSuccessModal,
  showUpdateEmailModal,
  setShowUpdateEmailModal,
}: Props) => {
  const [tabHasFocus, setTabHasFocus] = useState(true);
  const snackbar = useSnackBar();
  const [state, dispatch] = useContext(AppContext);
  const onOTPModalClose = () => {
    if (state.profileData?.email_verified)
      setShowVerificationSuccessModal && setShowVerificationSuccessModal(true);
    setIsOTPModalOpen && setIsOTPModalOpen(false);
  };
  const [updatedEmail, setUpdatedEmail] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const profile_slug = state.profileData?.slug;
  const [timerIds, setTimerIds] = useState<ReturnType<typeof setTimeout>[]>([]);
  const startTimers = () => {
    // clear all previous set of timers(if any) before starting new ones
    if (timerIds.length > 0) clearTimers();
    // if email gets verifed then dont start the timers
    if (state.profileData?.email_verified || showVerificationSuccessModal) {
      // console.log("Returning : profile state found!");
      return;
    }
    // if all modals are closed then don't start the timers
    if (!(showEmailVerifyModal || showUpdateEmailModal || isOTPModalOpen)) {
      // console.log("Returning : modal closed all 3!");
      return;
    }

    // console.log("Starting 5 timers...");
    const allTimerIds: ReturnType<typeof setTimeout>[] = [];
    [0, 0.5, 1, 3, 7, 12].map((value, i) => {
      const timerId: ReturnType<typeof setTimeout> = setTimeout(() => {
        console.log(i + 1 + ". Fetching isVerified value...");
        dispatch({
          type: ApplicationActionType.FETCH_PROFILE,
          payload: {},
        });
      }, value * 1000 * 60);
      allTimerIds.push(timerId);
    });
    setTimerIds(allTimerIds);
  };
  const clearTimers = () => {
    // console.log("----Clearing 5 timers...");
    timerIds.forEach((id) => {
      clearTimeout(id);
    });
  };
  useEffect(() => {
    try {
      const handleFocus = () => {
        setTabHasFocus(true);
      };
      const handleBlur = () => {
        setTabHasFocus(false);
      };
      window.addEventListener("focus", handleFocus);
      window.addEventListener("blur", handleBlur);

      return () => {
        window.removeEventListener("focus", handleFocus);
        window.removeEventListener("blur", handleBlur);
      };
    } catch (err) {
      //
    }
  }, []);

  useEffect(() => {
    if (tabHasFocus && isOTPModalOpen) {
      // console.log("Starting timers due to tab unfocused");
      startTimers();
    } else {
      // console.log("Clearing due to tab unfocused");
      clearTimers();
    }
  }, [tabHasFocus]);

  useEffect(() => {
    if (state.profileData?.email_verified) {
      isOTPModalOpen && onOTPModalClose();
      showUpdateEmailModal && setShowUpdateEmailModal && setShowUpdateEmailModal(false);
      showEmailVerifyModal && setShowEmailVerifyModal && setShowEmailVerifyModal(false);
      if (timerIds.length > 0) {
        // console.log("clearing timers due to getting profile data now!");
        clearTimers();
      }
    }
  }, [state.profileData?.email_verified]);

  const handleSendOTPEmailVerify = async () => {
    setLoading(true);
    // snackbar.info("Sending OTP!");
    if (profile_slug) {
      try {
        const new_email = updatedEmail ? updatedEmail : state.profileData?.email;
        const res = await sendEmailOtp(profile_slug, new_email);
        snackbar.info("OTP Sent!");
        setIsOTPModalOpen && setIsOTPModalOpen(true);
        setShowEmailVerifyModal && setShowEmailVerifyModal(false);
        updatedEmail && setShowUpdateEmailModal && setShowUpdateEmailModal(false);
        startTimers();
      } catch (err) {
        const e = axiosErrorHandler(err);
        if (typeof e === "object" && "message" in e) {
          snackbar.error(e.message);
        } else {
          snackbar.error("something went wrong");
        }
        console.error(err);
      }
    }
    setLoading(false);
  };
  const handleVerifyOTPEmailVerify = async (otp: string) => {
    //
    setLoading(true);
    if (!profile_slug) return;
    try {
      const res = await verifyEmailOtp(profile_slug, otp);
      snackbar.success("Verification Success!");
      onOTPModalClose();
      dispatch({
        type: ApplicationActionType.FETCH_PROFILE,
        payload: {},
      });
      // setShowVerificationSuccessModal && setShowVerificationSuccessModal(true);
    } catch (err) {
      const e = axiosErrorHandler(err);
      if (typeof e === "object" && "message" in e) {
        snackbar.error(e.message);
      } else {
        snackbar.error("something went wrong");
      }
    }
    setLoading(false);
  };

  const handleEmailEditClick = () => {
    setShowUpdateEmailModal && setShowUpdateEmailModal(true);
    onOTPModalClose();
  };
  // console.log("is window focused: ", windowFocused);
  const emailRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return (
    <>
      {/* Email Verify Modal */}
      <ModalTemplate
        title=""
        isOpen={showEmailVerifyModal ? showEmailVerifyModal : false}
        onClose={() => setShowEmailVerifyModal && setShowEmailVerifyModal(false)}
        marginX={2}
        maxWidth="464px"
        showCloseButtonIcon={true}
      >
        {
          <Box textAlign={{ base: "center", md: "left" }}>
            <EmailOutline2 boxSize="60px" color="ms-green.400" />
            <Heading mt={"ms-24"} as="h4">
              Verify your email address
            </Heading>
            <Text mt={"ms-8"} textStyle="body2">
              Hi {state.profileData?.name}. Please verify your email ID to continue your admission
              process and access self-learning modules.
            </Text>
            <Flex>
              <Button
                onClick={() => handleSendOTPEmailVerify()}
                mt={"ms-24"}
                variant="primary"
                size="md"
                mx={{ base: "auto", md: "0" }}
                ml={{ md: "auto" }}
                disabled={loading}
                position={loading ? "relative" : "static"}
              >
                Verify Email address
                {loading && (
                  <Spinner
                    speed={"0.5s"}
                    thickness={"2px"}
                    size={"md"}
                    position={"absolute"}
                    color={"ms-blue.500"}
                  />
                )}
              </Button>
            </Flex>
          </Box>
        }
      </ModalTemplate>
      {/* OTP MODAL */}
      {isOTPModalOpen && (
        <OTPModal
          isOpen={isOTPModalOpen}
          onClose={() => {
            // console.log("clearing timers due to otp modal closed!");
            clearTimers();
            dispatch({
              type: ApplicationActionType.FETCH_PROFILE,
              payload: {},
            });
            onOTPModalClose();
          }}
          title={"Email Verification"}
          primaryText="Please enter the OTP to verify your email address"
          secondaryText={
            <EmailEdit
              handleEmailEditClick={handleEmailEditClick}
              email={updatedEmail ? updatedEmail : state.profileData?.email}
            />
          }
          otpLength={6}
          timeGap={60}
          onComplete={(value) => {
            handleVerifyOTPEmailVerify(value);
          }}
          onResendCallback={async () => {
            handleSendOTPEmailVerify();
          }}
          loading={loading}
        />
      )}
      {/* Verification Success Modal */}
      <ModalTemplate
        title=""
        isOpen={showVerificationSuccessModal ? showVerificationSuccessModal : false}
        onClose={() => setShowVerificationSuccessModal && setShowVerificationSuccessModal(false)}
        marginX={2}
        maxWidth="464px"
        showCloseButtonIcon={true}
      >
        {
          <Box textAlign={"center"} pt={"ms-24"}>
            <Box
              m={"auto"}
              mb={{ base: "20px", md: "ms-32" }}
              w={{ base: "97px", md: "112px" }}
              h={{ base: "104px", md: "120px" }}
            >
              <Lottie loop animationData={successLottie} play style={{ width: "100%" }} />
            </Box>
            <Heading mb={"ms-8"} as="h3">
              Verification Completed!
            </Heading>
            <Text mb={"ms-24"} textStyle="body1" color="ms-grey.700">
              You can now continue to course selection. Thank you.
            </Text>
            <Button
              onClick={() => {
                setShowVerificationSuccessModal && setShowVerificationSuccessModal(false);
              }}
              size="md"
              variant="primary"
            >
              Continue to dashboard
            </Button>
          </Box>
        }
      </ModalTemplate>
      {/* Update Email Modal */}
      <ModalTemplate
        title=""
        isOpen={showUpdateEmailModal ? showUpdateEmailModal : false}
        onClose={() => {
          // console.log("clearing timers due to update email modal closed!");
          clearTimers();
          dispatch({
            type: ApplicationActionType.FETCH_PROFILE,
            payload: {},
          });
          setShowUpdateEmailModal && setShowUpdateEmailModal(false);
        }}
        marginX={2}
        maxWidth="464px"
        showCloseButtonIcon={true}
      >
        {
          <Box>
            <Heading as="h4">
              {state.profileData?.email ? "Update your email" : "Add your email"}
            </Heading>
            {state.profileData?.email && (
              <>
                <Text mt={"ms-16"} textStyle="body2" color="ms-grey.700">
                  Your current email address:
                </Text>
                <Text mb={"ms-8"} mt={"ms-4"} textStyle="body2-md" color="ms-grey.700">
                  {state.profileData?.email}
                </Text>
              </>
            )}
            <Text mt={"ms-24"} mb={"ms-8"} textStyle="body2-md" color="ms-grey.800">
              {state.profileData?.email
                ? "Please enter your new email address"
                : "Please enter your email address"}
            </Text>
            <InputCustom
              required
              type="email"
              isInvalid={updatedEmail !== "" && !emailRegex.test(updatedEmail)}
              getValueCallback={(value) => {
                setUpdatedEmail(value);
              }}
              errorMessage={"Please enter a valid email"}
              placeholder="Enter your new email address"
              onClick={() => {
                //
              }}
            />
            <Flex>
              <Button
                mt={updatedEmail !== "" && !emailRegex.test(updatedEmail) ? "14px" : "46px"}
                onClick={() => {
                  handleSendOTPEmailVerify();
                }}
                size="md"
                variant="primary"
                ml={"auto"}
                disabled={!updatedEmail || loading || !emailRegex.test(updatedEmail)}
                position={loading ? "relative" : "static"}
              >
                update & verify
                {loading && (
                  <Spinner
                    speed={"0.5s"}
                    thickness={"2px"}
                    size={"md"}
                    position={"absolute"}
                    color={"ms-blue.500"}
                  />
                )}
              </Button>
            </Flex>
          </Box>
        }
      </ModalTemplate>
    </>
  );
};

export default EmailVerifyModal;
